import { Thermometer } from "lucide-react";
import { useGetList, useTranslate } from "ra-core";
import { Card } from "@/components/ui/card";

import type { TemperatureSummaryRow } from "../types";
import { TemperatureBadge } from "./TemperatureBadge";
import type { Temperature } from "./temperatureModel";

const LEAD_TEMPERATURES: readonly Temperature[] = ["quente", "morno", "frio"];

const BAR_COLOR: Record<Temperature, string> = {
  quente: "bg-red-500",
  morno: "bg-amber-400",
  frio: "bg-sky-500",
  cliente: "bg-emerald-500",
};

// [hiperautomacao] Dashboard card: how the leads split between hot, warm
// and cold, next to the Accelera reference (5 / 45 / 50 by default,
// configurable in temperatura.referencia).
export const TemperatureSummary = () => {
  const translate = useTranslate();
  const { data, isPending } = useGetList<TemperatureSummaryRow>(
    "temperature_summary",
    { pagination: { page: 1, perPage: 10 } },
  );
  if (isPending) return null;

  const rowFor = (temperature: Temperature) =>
    data?.find((row) => row.id === temperature);
  const leadTotal = LEAD_TEMPERATURES.reduce(
    (sum, temperature) => sum + Number(rowFor(temperature)?.people ?? 0),
    0,
  );
  const clients = Number(rowFor("cliente")?.people ?? 0);
  const corrected = (data ?? []).reduce(
    (sum, row) => sum + Number(row.corrected ?? 0),
    0,
  );

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center">
        <div className="mr-3 flex">
          <Thermometer className="text-muted-foreground w-6 h-6" />
        </div>
        <h2 className="text-xl font-semibold text-muted-foreground">
          {translate("hiper.temperature.summary.title")}
        </h2>
      </div>
      <Card className="px-4 py-3 gap-3">
        {leadTotal === 0 ? (
          <p className="text-sm text-muted-foreground">
            {translate("hiper.temperature.summary.empty")}
          </p>
        ) : (
          <ul className="flex flex-col gap-3">
            {LEAD_TEMPERATURES.map((temperature) => {
              const row = rowFor(temperature);
              const people = Number(row?.people ?? 0);
              const percent = Math.round((people / leadTotal) * 100);
              const reference = row?.reference ?? null;
              return (
                <li
                  key={temperature}
                  className="flex flex-col gap-1"
                  data-testid={`temperature-summary-${temperature}`}
                >
                  <div className="flex items-center justify-between gap-2 text-sm">
                    <TemperatureBadge temperature={temperature} />
                    <span className="tabular-nums">
                      {percent}% ·{" "}
                      {translate("hiper.temperature.summary.leads", {
                        smart_count: people,
                      })}
                      {reference != null ? (
                        <span className="text-muted-foreground">
                          {" "}
                          ·{" "}
                          {translate("hiper.temperature.summary.target", {
                            value: Number(reference),
                          })}
                        </span>
                      ) : null}
                    </span>
                  </div>
                  <div
                    className="relative h-2 rounded-full bg-muted overflow-hidden"
                    aria-hidden="true"
                  >
                    <div
                      className={`h-full ${BAR_COLOR[temperature]}`}
                      style={{ width: `${percent}%` }}
                    />
                    {reference != null ? (
                      <div
                        className="absolute top-0 h-full w-0.5 bg-foreground/70"
                        style={{ left: `${Math.min(Number(reference), 100)}%` }}
                      />
                    ) : null}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
        {leadTotal > 0 && data?.some((row) => row.reference != null) ? (
          <p className="text-xs text-muted-foreground">
            {translate("hiper.temperature.summary.target_legend")}
          </p>
        ) : null}
        <div className="flex flex-wrap justify-between gap-2 text-xs text-muted-foreground border-t pt-2">
          <span>
            {translate("hiper.temperature.summary.clients", {
              smart_count: clients,
            })}
          </span>
          {corrected > 0 ? (
            <span>
              {translate("hiper.temperature.summary.corrected", {
                smart_count: corrected,
              })}
            </span>
          ) : null}
        </div>
      </Card>
    </div>
  );
};
